// ZadrzneView.jsx — samostatná stránka „Zádržné firmy" (ústava 17.5)
// Kompletný zoznam všetkých FA so zádržným · filter podľa stavu · klik → FaktDetail.
import React, { useState, useEffect, useMemo } from 'react'
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { useNavigate } from 'react-router-dom'
import { COLORS, TYPO, SPACE, RADIUS, SK, todayLocal, hladaj } from '../theme.js'
import { PageHeader, KpiRow, KpiCard, FilterChips, SearchInput } from '../components/Ui.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useAuth } from '../auth/AuthContext.jsx'

const ZADRZNE_ROLES = ['admin','owner','owner_view','office_manager','accountant']

const STATUS_LABEL = {
  zadrzane:    'Zadržané',
  uvolnitelne: 'Uvoľniteľné',
  fakturovane: 'Fakturované',
  uhradene:    'Uhradené',
}
const STATUS_COLOR = {
  zadrzane:    '#f59e0b',
  uvolnitelne: '#3b82f6',
  fakturovane: '#7c3aed',
  uhradene:    '#10b981',
}

const FILTERS = [
  { key: 'all',         label: 'Všetko',       color: COLORS.muted },
  { key: 'zadrzane',    label: 'Zadržané',     color: '#f59e0b' },
  { key: 'uvolnitelne', label: 'Uvoľniteľné',  color: '#3b82f6' },
  { key: 'fakturovane', label: 'Fakturované',  color: '#7c3aed' },
  { key: 'uhradene',    label: 'Uhradené',     color: '#10b981' },
  { key: 'upcoming',    label: 'Do 30 dní',  color: '#f59e0b' },
]

export default function ZadrzneView() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!user || !ZADRZNE_ROLES.includes(user.role)) return
    fetchAuth('/api/zadrzne/stats')
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [user])
  useLiveRefresh(() => { if (user && ZADRZNE_ROLES.includes(user.role)) fetchAuth('/api/zadrzne/stats').then(r => r.ok ? r.json() : null).then(setData).catch(() => {}) }) // živé

  const items = data?.items || []
  const today = todayLocal()
  const in30  = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter(r => {
      if (q && !hladaj(`${r.invoice_number} ${r.id} ${r.source}`, q)) return false
      if (filter === 'all') return true
      if (filter === 'upcoming') {
        const sUp = r.zadrzne_release_short && r.zadrzne_release_short <= in30 && r.zadrzne_release_short >= today && r.zadrzne_status_short === 'zadrzane'
        const lUp = r.zadrzne_release_long  && r.zadrzne_release_long  <= in30 && r.zadrzne_release_long  >= today && r.zadrzne_status_long  === 'zadrzane'
        return sUp || lUp
      }
      return r.zadrzne_status_short === filter || r.zadrzne_status_long === filter
    })
  }, [items, filter, search, today, in30])

  if (!user || !ZADRZNE_ROLES.includes(user.role)) {
    return <div style={{ padding: SPACE.xl, color: COLORS.muted }}>
      Nemáš oprávnenie zobraziť zádržné firmy.
    </div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PageHeader title="Zádržné firmy" subtitle="Ústava 17.5 — anti-strata · klikni na riadok → FaktDetail" />
      {loading ? (
        <div style={{ padding: SPACE.xl, color: COLORS.muted }}>Načítavam…</div>
      ) : !data ? (
        <div style={{ padding: SPACE.xl, color: COLORS.remove }}>Dáta sa nenačítali.</div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: SPACE.lg }}>

          {/* KPI */}
          <KpiRow cols={4}>
            <KpiCard title="Zadržané"     value={SK.eur(data.sum_zadrzane)}    color="#f59e0b" highlight />
            <KpiCard title="Uvoľniteľné"  value={SK.eur(data.sum_uvolnitelne)} color="#3b82f6" />
            <KpiCard title="Fakturované"  value={SK.eur(data.sum_fakturovane)} color="#7c3aed" />
            <KpiCard title="Uhradené"     value={SK.eur(data.sum_uhradene)}    color="#10b981" />
          </KpiRow>

          {/* Filter + search */}
          <div style={{ display: 'flex', gap: SPACE.md, alignItems: 'center' }}>
            <FilterChips options={FILTERS} value={filter} onChange={setFilter} />
            <div style={{ flex: 1 }} />
            <SearchInput value={search} onChange={setSearch} placeholder="č. faktúry / ID…" />
          </div>

          {/* Upcoming alert */}
          {data.upcoming_30d && data.upcoming_30d.length > 0 && filter !== 'upcoming' && (
            <div style={{
              padding: SPACE.md,
              background: 'rgba(245,158,11,0.12)',
              border: `1px solid #f59e0b`, borderRadius: RADIUS.md,
              display: 'flex', alignItems: 'center', gap: SPACE.md,
            }}>
              <div style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold, color: '#f59e0b', flex: 1 }}>
                {data.upcoming_30d.length} FA má zádržné uvoľniteľné do 30 dní — nárokuj zádržné (anti-strata, ústava 17.5)
              </div>
              <button onClick={() => setFilter('upcoming')}
                style={{
                  background: '#f59e0b', color: '#fff', border: 'none',
                  borderRadius: RADIUS.md, padding: '6px 14px',
                  fontWeight: TYPO.weights.bold, cursor: 'pointer', fontFamily: 'inherit',
                }}>Zobraziť →</button>
            </div>
          )}

          {/* Tabuľka */}
          <div style={{
            background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg,
            overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
              <thead>
                <tr style={{ background: COLORS.bgSoft, borderBottom: `2px solid ${COLORS.line}` }}>
                  <Th>č. FA</Th>
                  <Th>Zdroj</Th>
                  <Th align="right">Suma FA</Th>
                  <Th align="center">Krátkodobé</Th>
                  <Th align="center">Dlhodobé</Th>
                  <Th align="right">Zádržné spolu</Th>
                  <Th>Uvoľnenie</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: SPACE.xl, textAlign: 'center', color: COLORS.muted }}>
                    Žiadne FA pre tento filter.
                  </td></tr>
                ) : filtered.map(r => {
                  const total = (Number(r.zadrzne_kratkodoba_sum) || 0) + (Number(r.zadrzne_dlhodoba_sum) || 0)
                  return (
                    <tr key={r.source + '-' + r.id}
                      // odkazy_v1: Faktúry držia id s prefixom zdroja ('M:' MKsoft, 'O:' Matrix). Bez neho
                      // pravý panel ostal na „Vyber faktúru zo zoznamu" a klik na zádržné nerobil nič.
                      onClick={() => navigate('/faktury/' + (r.source === 'mksoft' ? 'M:' : 'O:') + r.id)}
                      style={{
                        borderBottom: `1px solid ${COLORS.lineSoft}`,
                        cursor: 'pointer',
                      }}>
                      <td style={tdMono}>{r.invoice_number}</td>
                      <td style={tdMuted}>{r.source === 'mksoft' ? 'MKsoft' : 'Matrix'}</td>
                      <td style={{ ...tdMono, textAlign: 'right' }}>{SK.eur(r.amount_total)}</td>
                      <td style={{ ...tdCell, textAlign: 'center' }}>
                        {Number(r.zadrzne_kratkodoba_sum) > 0 ? (
                          <StatusBlock pct={r.zadrzne_kratkodoba_pct} sum={r.zadrzne_kratkodoba_sum} stat={r.zadrzne_status_short} />
                        ) : '—'}
                      </td>
                      <td style={{ ...tdCell, textAlign: 'center' }}>
                        {Number(r.zadrzne_dlhodoba_sum) > 0 ? (
                          <StatusBlock pct={r.zadrzne_dlhodoba_pct} sum={r.zadrzne_dlhodoba_sum} stat={r.zadrzne_status_long} />
                        ) : '—'}
                      </td>
                      <td style={{ ...tdMono, textAlign: 'right', fontWeight: TYPO.weights.bold, color: '#f59e0b' }}>
                        {SK.eur(total)}
                      </td>
                      <td style={tdCell}>
                        {[r.zadrzne_release_short, r.zadrzne_release_long].filter(Boolean).join(' · ') || '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

const tdCell  = { padding: '8px 10px', fontSize: TYPO.sizes.sm }
const tdMono  = { ...tdCell, fontFamily: TYPO.mono }
const tdMuted = { ...tdCell, color: COLORS.muted, fontSize: TYPO.sizes.xs }

function Th({ children, align = 'left' }) {
  return <th style={{
    padding: '8px 10px', textAlign: align,
    color: COLORS.muted, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
    textTransform: 'uppercase', letterSpacing: '0.06em',
  }}>{children}</th>
}

function StatusBlock({ pct, sum, stat }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
      <span style={{ fontFamily: TYPO.mono, fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold }}>
        {Number(pct).toFixed(1)} % · {SK.eur(sum)}
      </span>
      <span style={{
        fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
        color: STATUS_COLOR[stat] || COLORS.muted,
        padding: '1px 6px', borderRadius: RADIUS.full,
        border: `1px solid ${STATUS_COLOR[stat] || COLORS.muted}`,
      }}>{STATUS_LABEL[stat] || stat || '—'}</span>
    </div>
  )
}
